import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import {
  FileText,
  Clock,
  Plus,
  MapPin,
  XCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { getStatusText, formatDate } from '../utils/helpers';

const Dashboard = () => {
  const { user } = useAuth();

  const { data: dashboardData, isLoading } = useQuery(
    'dashboard',
    async () => {
      if (['eac', 'sdo', 'adc', 'dc'].includes(user.role)) {
        const response = await api.get('/reports/dashboard');
        return response.data.dashboard;
      } else {
        const response = await api.get('/permits?limit=5');
        return { recentPermits: response.data.permits };
      }
    },
    {
      enabled: !!user,
      refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    }
  );

  // Fetch pending cancellation requests for reviewers
  const { data: pendingCancellationsData } = useQuery(
    ['pendingCancellations', 'mine'],
    async () => {
      const response = await api.get('/cancellation-requests/pending/mine');
      return response.data.cancellationRequests;
    },
    {
      enabled: !!user && ['eac', 'sdo', 'adc', 'dc'].includes(user.role),
      refetchInterval: 30000 // Refetch every 30 seconds
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const isReviewer = ['eac', 'sdo', 'adc', 'dc'].includes(user.role);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-secondary-900">
              Welcome back, {user.fullName}
            </h1>
            <p className="text-secondary-600 mt-1 text-sm">
              {user.role === 'bo' && 'Manage your land permit applications'}
              {user.role === 'eac' && 'Review and approve permits for SDO review'}
              {user.role === 'sdo' && 'Review and approve permits for ADC review'}
              {user.role === 'adc' && 'Review and approve permits for DC approval'}
              {user.role === 'dc' && 'Final approval and system administration'}
            </p>
          </div>
          {['bo', 'eac', 'sdo', 'adc', 'dc'].includes(user.role) && (
            <Link
              to="/permits/new"
              className="btn btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Permit
            </Link>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions Card */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-base font-medium text-secondary-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            {['bo', 'eac', 'sdo', 'adc', 'dc'].includes(user.role) && (
              <Link
                to="/permits/new"
                className="flex items-center p-3 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
              >
                <Plus className="h-5 w-5 text-primary-600 mr-3" />
                <span className="text-primary-900 font-medium text-sm">Create New Permit</span>
              </Link>
            )}

            <Link
              to="/permits"
              className="flex items-center p-3 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors"
            >
              <FileText className="h-5 w-5 text-secondary-600 mr-3" />
              <span className="text-secondary-900 font-medium text-sm">View All Permits</span>
            </Link>

            {['eac', 'sdo', 'adc', 'dc'].includes(user.role) && (
              <Link
                to="/workflow"
                className="flex items-center p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors"
              >
                <Clock className="h-5 w-5 text-yellow-600 mr-3" />
                <span className="text-yellow-900 font-medium text-sm">Pending Reviews</span>
              </Link>
            )}

            <Link
              to="/search"
              className="flex items-center p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
            >
              <MapPin className="h-5 w-5 text-green-600 mr-3" />
              <span className="text-green-900 font-medium text-sm">Public Search</span>
            </Link>
          </div>
        </div>

        {/* Recent Permits */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-medium text-secondary-900">Recent Permits</h3>
            <Link
              to="/permits"
              className="text-primary-600 hover:text-primary-500 text-sm font-medium"
            >
              View all
            </Link>
          </div>

          {dashboardData?.recentPermits?.length > 0 ? (
            <div className="space-y-3">
              {(dashboardData.recentPermits || []).slice(0, 5).map((permit) => (
                <div
                  key={permit.id}
                  className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/permits/${permit.id}`}
                      className="text-sm font-medium text-secondary-900 hover:text-primary-600 truncate block"
                    >
                      {permit.permit_no}
                    </Link>
                    <p className="text-sm text-secondary-600 truncate">{permit.name}</p>
                    <p className="text-xs text-secondary-500">{formatDate(permit.createdAt)}</p>
                  </div>
                  <span className={`status-badge ${getStatusColor(permit.status)}`}>
                    {getStatusText(permit.status)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-secondary-300 mx-auto mb-3" />
              <p className="text-secondary-500">No permits found</p>
              {['bo', 'eac', 'sdo', 'adc', 'dc'].includes(user.role) && (
                <Link
                  to="/permits/new"
                  className="text-primary-600 hover:text-primary-500 text-sm font-medium"
                >
                  Create your first permit
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Pending Cancellation Requests for Reviewers */}
      {isReviewer && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <h3 className="text-base font-medium text-secondary-900">Pending Cancellation Requests</h3>
              {pendingCancellationsData && pendingCancellationsData.length > 0 && (
                <span className="ml-2 bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-sm font-medium">
                  {pendingCancellationsData.length}
                </span>
              )}
            </div>
            <Link
              to="/cancellation-requests"
              className="text-primary-600 hover:text-primary-500 text-sm font-medium"
            >
              View all
            </Link>
          </div>

          {pendingCancellationsData && pendingCancellationsData.length > 0 ? (
            <div className="space-y-3">
              {pendingCancellationsData.map(req => (
                <div
                  key={req.id}
                  className="flex items-start justify-between p-4 border border-red-200 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <XCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                      <Link
                        to={`/permits/${req.permitId}`}
                        className="text-sm font-medium text-secondary-900 hover:text-primary-600"
                      >
                        {req.permit?.permit_no}
                      </Link>
                    </div>
                    <p className="text-sm text-secondary-700">{req.permit?.name}</p>
                    <p className="text-xs text-secondary-600 mt-1">
                      Initiated by {req.initiator?.fullName} ({req.initiatorRole.toUpperCase()})
                    </p>
                    <p className="text-sm text-secondary-700 mt-2 line-clamp-2">
                      <span className="font-medium">Reason:</span> {req.reason}
                    </p>
                  </div>
                  <div className="ml-4 flex flex-col items-end gap-2">
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded whitespace-nowrap">
                      {req.status.replace(/_/g, ' ')}
                    </span>
                    <Link
                      to={`/cancellation-requests/${req.id}`}
                      className="btn btn-sm btn-primary whitespace-nowrap"
                    >
                      Review
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <XCircle className="h-12 w-12 text-secondary-300 mx-auto mb-3" />
              <p className="text-secondary-500">No pending cancellation requests</p>
            </div>
          )}
        </div>
      )}

      {/* Status Overview for Reviewers */}
      {isReviewer && dashboardData?.permitsByStatus && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-base font-medium text-secondary-900 mb-4">Permits by Status</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Object.entries(dashboardData.permitsByStatus).map(([status, count]) => (
              <div key={status} className="text-center p-4 bg-secondary-50 rounded-lg">
                <div className="text-2xl font-bold text-secondary-900">{count}</div>
                <div className="text-sm text-secondary-600 capitalize">
                  {getStatusText(status)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const getStatusColor = (status) => {
  const colors = {
    draft: 'status-draft',
    submitted: 'status-submitted',
    under_review: 'status-under-review',
    approved: 'status-approved',
    rejected: 'status-rejected',
    returned: 'status-returned'
  };
  return colors[status] || 'status-draft';
};

export default Dashboard;